import { useAnalytics } from "@codat/analytics";
import { Spinner, SyncIcon, useToast } from "@codat/orchard-ui";
import { useQueueDataRefreshMutation } from "api/mutations/useQueueDataRefreshMutation";
import {
  HistoricalSynchronizationResponse,
  useActiveSynchronizationQuery,
  useLatestSynchronizationQuery,
} from "api/queries/useGetHistoricalSyncsQuery";
import { PagedResponse } from "api/shared";
import { AxiosResponse } from "axios";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { Subject } from "rxjs";

import Button from "components/Button";

import {
  WindowEventService,
  WindowEvents,
} from "./../../../../services/WindowEventService";
import styles from "./RefreshData.module.css";
import RefreshDataModal from "./RefreshDataModal";

const REFRESH_WARNING_THRESHOLD_HOURS = 24;

const getHoursSinceLastSynchronization = (
  lastSynchronization: Date
): number => {
  const lastSynchronizationTime = new Date(lastSynchronization).getTime();
  const currentTime = new Date().getTime();
  const hoursDiff = (currentTime - lastSynchronizationTime) / (1000 * 60 * 60);
  return hoursDiff;
};

interface RefreshDataProps {
  companyId: string;
  companyName?: string;
  pollRate: number;
  loading: boolean;
  onRefreshClick: () => boolean;
  queueSync$: Subject<void>;
}

const RefreshData: React.FC<RefreshDataProps> = (props) => {
  const {
    companyId,
    companyName,
    pollRate,
    loading,
    onRefreshClick,
    queueSync$,
  } = props;
  const [
    { data: latestSynchronization, error: latestSynchronizationError },
    getLatestSynchronization,
  ] = useLatestSynchronizationQuery(companyId);
  const [
    {
      data: activeSynchronization,
      loading: synchronizationRunningQueryLoading,
      error: activeSynchronizationError,
    },
    getActiveSynchronization,
  ] = useActiveSynchronizationQuery(companyId, true);
  const { sendEvent } = useAnalytics();
  const [, queueDataRefresh] = useQueueDataRefreshMutation(companyId);

  const { addToast } = useToast();

  const [showRefreshDataModal, setShowRefreshDataModal] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [doPoll, setDoPoll] = useState(false);
  const [hideSuccessToastUponCompletion, setHideSuccessToastUponCompletion] =
    useState(false);

  const showSkeleton =
    !doPoll && (synchronizationRunningQueryLoading || loading);

  useEffect(() => {
    WindowEventService.subscribe(
      WindowEvents.PollForCompanyDataRefresh,
      handlePollForCompanyDataRefresh
    );

    return () => {
      WindowEventService.unsubscribe(
        WindowEvents.PollForCompanyDataRefresh,
        handlePollForCompanyDataRefresh
      );
    };
  });

  const onRefreshDataClick = () => {
    const latestSynchronizationDate =
      latestSynchronization?.results?.[0]?.requested;

    if (!latestSynchronizationDate) {
      refreshData();
      return;
    }

    const hoursSinceLastSynchronization = getHoursSinceLastSynchronization(
      latestSynchronizationDate
    );

    if (hoursSinceLastSynchronization < REFRESH_WARNING_THRESHOLD_HOURS) {
      setShowRefreshDataModal(true);
    } else {
      refreshData();
    }
  };

  const refreshData = () => {
    setDataLoading(true);
    setHideSuccessToastUponCompletion(false);
    queueDataRefresh()
      .then(() => {
        setDoPoll(true);
        queueSync$.next();
      })
      .catch(() => {
        addToast({
          variant: "error",
          message: "An error occured",
          subText: `Could not refresh data for company ${companyName}`,
        });
        setDataLoading(false);
      });
  };

  const handlePollForCompanyDataRefresh = () => {
    setDoPoll(true);
    setDataLoading(true);
    setHideSuccessToastUponCompletion(true);
  };

  useEffect(() => {
    if (!latestSynchronization && !latestSynchronizationError) {
      getLatestSynchronization();
    }
  }, [
    latestSynchronization,
    latestSynchronizationError,
    getLatestSynchronization,
  ]);

  useEffect(() => {
    getActiveSynchronization()
      .then(() => setDataLoading(false))
      .catch(() => setDataLoading(false));
  }, [getActiveSynchronization, setDoPoll, setDataLoading]);

  useEffect(() => {
    if (activeSynchronization?.totalResults && !activeSynchronizationError) {
      if (!doPoll) {
        setDoPoll(true);
      }
      if (!dataLoading) {
        setDataLoading(true);
      }
    }
  }, [
    setDoPoll,
    doPoll,
    dataLoading,
    activeSynchronizationError,
    activeSynchronization,
  ]);

  useEffect(() => {
    const stopPolling = () => {
      if (dataLoading) {
        setDataLoading(false);
      }
      if (doPoll) {
        setDoPoll(false);
      }
    };

    const showSuccessToast = () =>
      !hideSuccessToastUponCompletion &&
      addToast({
        subText: `${companyName} has new data, click below to view`,
        message: "Refresh complete",
        ctas: [
          {
            primary: true,
            text: "Refresh page",
            onClick: () => {
              sendEvent("Click - Refresh Page Button", { companyId });
              return onRefreshClick();
            },
          },
        ],
        variant: "success",
      });

    const handleActiveSynchronizationResponse = (
      response: AxiosResponse<PagedResponse<HistoricalSynchronizationResponse>>
    ) => {
      if (!response.data.totalResults) {
        stopPolling();
        getLatestSynchronization();
        showSuccessToast();
      }
    };

    const handleActiveSynchronizationError = (reason: string) => {
      stopPolling();
      if (reason.includes("404")) {
        getLatestSynchronization();
        showSuccessToast();
      }
    };

    if (doPoll) {
      const timeoutRef = setTimeout(() => {
        getActiveSynchronization()
          .then(handleActiveSynchronizationResponse)
          .catch((reason) =>
            handleActiveSynchronizationError(reason.toString())
          );
      }, pollRate);
      return () => {
        clearTimeout(timeoutRef);
      };
    }
  }, [
    getLatestSynchronization,
    getActiveSynchronization,
    doPoll,
    activeSynchronization,
    companyId,
    dataLoading,
    onRefreshClick,
    addToast,
    pollRate,
    companyName,
    sendEvent,
    hideSuccessToastUponCompletion,
  ]);

  return (
    <div className={classNames(styles.syncButtonWrapper, styles.top)}>
      {!dataLoading && (
        <Button
          variant="secondary"
          color="interface"
          eventProperties={{ companyId }}
          buttonName="Refresh data"
          data-testid="refresh-data-button"
          onClick={onRefreshDataClick}
          isSkeleton={showSkeleton}
          disabled={showSkeleton}
        >
          <SyncIcon />
          <span className={styles.syncButtonText}>Refresh data</span>
        </Button>
      )}
      {dataLoading && (
        <Button
          buttonName="Refresh data"
          eventProperties={{ companyId }}
          color="interface"
          data-testid="refreshing-data-button"
          variant="secondary"
          disabled={true}
        >
          <Spinner className={styles.syncButtonSpinner} />
          <span className={styles.syncButtonLoading}>Refreshing...</span>
        </Button>
      )}
      {showRefreshDataModal && (
        <RefreshDataModal
          onCancel={() => {
            sendEvent("Click - Refresh Data Cancellation", { companyId });
            setShowRefreshDataModal(false);
          }}
          onRefreshData={() => {
            sendEvent("Click - Refresh Data Confirmation", { companyId });
            refreshData();
            setShowRefreshDataModal(false);
          }}
        />
      )}
    </div>
  );
};

export default RefreshData;
